import { DateTime } from 'luxon';

/**
 * Get the nightly rate for a specific date.
 * Falls back to baseRate if no override exists in datePricing.
 */
export const getNightlyRate = (dateString, datePricing, baseRate) => {
    if (datePricing && datePricing[dateString] !== undefined) {
        return parseFloat(datePricing[dateString]);
    }
    return parseFloat(baseRate) || 0;
};

/**
 * Generate array of ISO date strings for each night of a stay.
 * Check-in through check-out minus 1 day (you pay for the night you sleep, not departure day).
 */
export const getStayNights = (checkInDate, checkOutDate) => {
    const start = DateTime.fromISO(checkInDate);
    const end = DateTime.fromISO(checkOutDate);
    const nights = [];
    let current = start;
    while (current < end) {
        nights.push(current.toISODate());
        current = current.plus({ days: 1 });
    }
    return nights;
};

/**
 * Convert luxon weekday (1=Mon...7=Sun) to JS-style (0=Sun...6=Sat).
 */
const toJsWeekday = (luxonWeekday) => {
    return luxonWeekday === 7 ? 0 : luxonWeekday;
};

/**
 * Calculate the total accommodation cost for a date range using per-date pricing.
 * Returns a detailed breakdown object.
 *
 * @param {string} checkInDate - ISO date string "YYYY-MM-DD"
 * @param {string} checkOutDate - ISO date string "YYYY-MM-DD"
 * @param {object} villaData - { villaRentalFee, datePricing, cleaningFee, bookingRules }
 * @returns {{ total, subtotal, nightlyBreakdown, warnings, discount, cleaningFee, nightCount }}
 */
export const calculateDateBasedAccommodation = (checkInDate, checkOutDate, villaData) => {
    const { villaRentalFee, datePricing, cleaningFee, bookingRules } = villaData;
    const baseRate = parseFloat(villaRentalFee) || 0;
    const cleaning = parseFloat(cleaningFee) || 0;
    const nights = getStayNights(checkInDate, checkOutDate);
    const warnings = [];

    // Build nightly breakdown
    const nightlyBreakdown = nights.map(dateStr => {
        const rate = getNightlyRate(dateStr, datePricing, baseRate);
        const isOverride = datePricing && datePricing[dateStr] !== undefined;
        const isBlackout = bookingRules?.blackoutDates?.includes(dateStr);
        const isCallToBook = bookingRules?.callToBookDates?.includes(dateStr);
        const note = bookingRules?.dateNotes?.[dateStr] || null;

        if (isBlackout) {
            warnings.push({ date: dateStr, type: 'blackout', message: `${dateStr} is a blackout date` });
        }
        if (isCallToBook) {
            warnings.push({ date: dateStr, type: 'callToBook', message: `${dateStr} requires call to book` });
        }

        return { date: dateStr, rate, isOverride, isBlackout, isCallToBook, note };
    });

    // Validate booking rules
    if (bookingRules) {
        if (bookingRules.minNights && nights.length < bookingRules.minNights) {
            warnings.push({
                type: 'minNights',
                message: `Minimum ${bookingRules.minNights} nights required (currently ${nights.length})`
            });
        }
        if (bookingRules.maxNights && nights.length > bookingRules.maxNights) {
            warnings.push({
                type: 'maxNights',
                message: `Maximum ${bookingRules.maxNights} nights exceeded (currently ${nights.length})`
            });
        }
        if (bookingRules.checkInDays?.length > 0) {
            const jsDow = toJsWeekday(DateTime.fromISO(checkInDate).weekday);
            if (!bookingRules.checkInDays.includes(jsDow)) {
                const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const allowed = bookingRules.checkInDays.map(d => dayNames[d]).join(', ');
                warnings.push({
                    type: 'checkInDay',
                    message: `Check-in only allowed on: ${allowed}`
                });
            }
        }
        if (bookingRules.checkOutDays?.length > 0) {
            const jsDow = toJsWeekday(DateTime.fromISO(checkOutDate).weekday);
            if (!bookingRules.checkOutDays.includes(jsDow)) {
                const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const allowed = bookingRules.checkOutDays.map(d => dayNames[d]).join(', ');
                warnings.push({
                    type: 'checkOutDay',
                    message: `Check-out only allowed on: ${allowed}`
                });
            }
        }
    }

    // Sum nightly prices
    const subtotal = nightlyBreakdown.reduce((sum, night) => sum + night.rate, 0);

    // Apply night-count discount (largest matching tier)
    let discount = { applied: false, description: '', amount: 0 };
    if (bookingRules?.nightDiscounts?.length > 0) {
        const sortedDiscounts = [...bookingRules.nightDiscounts].sort((a, b) => b.minNights - a.minNights);
        const matchingDiscount = sortedDiscounts.find(d => nights.length >= d.minNights);
        if (matchingDiscount) {
            if (matchingDiscount.type === 'percentage') {
                discount = {
                    applied: true,
                    description: `${matchingDiscount.value}% off for ${matchingDiscount.minNights}+ nights`,
                    amount: subtotal * (matchingDiscount.value / 100)
                };
            } else {
                discount = {
                    applied: true,
                    description: `${matchingDiscount.value} off for ${matchingDiscount.minNights}+ nights`,
                    amount: parseFloat(matchingDiscount.value)
                };
            }
        }
    }

    const total = subtotal - discount.amount + cleaning;

    return { total, subtotal, nightlyBreakdown, warnings, discount, cleaningFee: cleaning, nightCount: nights.length };
};

/**
 * Check if a villa has date-based pricing configured.
 */
export const hasDatePricing = (villaData) => {
    const pricing = villaData?.datePricing?.value || villaData?.datePricing;
    return pricing && typeof pricing === 'object' && Object.keys(pricing).length > 0;
};

/**
 * Check if a guide has check-in/check-out dates set.
 */
export const hasDateRange = (guideData) => {
    return !!(guideData?.checkInDate?.value && guideData?.checkOutDate?.value);
};

/**
 * Get all dates in a given month as ISO date strings.
 */
export const getMonthDates = (year, month) => {
    const start = DateTime.local(year, month, 1);
    const end = start.endOf('month');
    const dates = [];
    let current = start;
    while (current <= end) {
        dates.push(current.toISODate());
        current = current.plus({ days: 1 });
    }
    return dates;
};

/**
 * Get weekday dates (Mon-Fri) in a given month.
 */
export const getWeekdayDates = (year, month) => {
    return getMonthDates(year, month).filter(dateStr => {
        const dow = DateTime.fromISO(dateStr).weekday;
        return dow >= 1 && dow <= 5;
    });
};

/**
 * Get weekend dates (Sat-Sun) in a given month.
 */
export const getWeekendDates = (year, month) => {
    return getMonthDates(year, month).filter(dateStr => {
        const dow = DateTime.fromISO(dateStr).weekday;
        return dow === 6 || dow === 7;
    });
};
